
import { supabase } from "@/integrations/supabase/client";
import { devLog, devError } from "@/utils/devLog";

export interface StudentKpi {
  totalStudents: number;
  newEnrollments: number;
  activeStudents: number;
  averageProgress: number;
  totalRevenue: number;
  arps: number;
}

export const fetchStudentKpi = async (creatorId: string, periodDays: number = 30): Promise<{
  data: StudentKpi;
  error: any;
}> => {
  devLog(`Fetching student KPIs for creator ${creatorId} with period ${periodDays} days `);
  
  // Ensure periodDays is a number
  const periodDaysNum = typeof periodDays === 'string' ? parseInt(periodDays, 10) : periodDays;
  
  console.log("[KPI Service] Requesting student KPIs with:", {
    p_creator_id: creatorId,
    period_days: periodDaysNum
  });

  try {
    // First try to use the RPC function directly as it's more efficient
    console.log("[KPI Service] Calling RPC function get_students_kpis");
    const result = await supabase.rpc('get_students_kpis', {
      p_creator_id: creatorId, // Use p_creator_id to match the SQL function parameter
      period_days: periodDaysNum 
    });
    
    // Log RPC result for debugging
    if (process.env.NODE_ENV === 'development') {
      console.group(`[RPC Debug] get_students_kpis`);
      console.log('Status:', result.status);
      console.log('Success:', !result.error);
      if (result.error) {
        console.error('Error:', result.error);
      }
      console.groupEnd();
    }
    
    console.log("[KPI Service] RPC response:", result);
    
    if (result.error) {
      devError("Error from RPC, falling back to Edge Function:", result.error);
      console.error("[KPI Service] RPC error details:", {
        code: result.error.code,
        message: result.error.message,
        details: result.error.details
      });
      
      // Fall back to edge function if RPC fails
      console.log("[KPI Service] Falling back to edge function");
      const edgeFnResult = await supabase.functions.invoke('get-students-kpi', {
        body: {
          creator_id: creatorId,
          period_days: periodDaysNum
        }
      });
      
      // Log edge function result
      if (process.env.NODE_ENV === 'development') {
        console.group(`[RPC Debug] get-students-kpi edge function`);
        console.log('Success:', !edgeFnResult.error);
        if (edgeFnResult.error) {
          console.error('Error:', edgeFnResult.error);
        }
        console.groupEnd();
      }
      
      console.log("[KPI Service] Edge function response:", edgeFnResult);
      
      if (edgeFnResult.error) {
        devError("Error fetching student KPIs via edge function:", edgeFnResult.error);
        return {
          data: {
            totalStudents: 0,
            newEnrollments: 0,
            activeStudents: 0,
            averageProgress: 0,
            totalRevenue: 0,
            arps: 0
          },
          error: edgeFnResult.error
        };
      }
      
      // Map the response from the edge function
      const rawData = edgeFnResult.data as any;
      const parsedData: StudentKpi = {
        totalStudents: rawData?.total_students || 0,
        newEnrollments: rawData?.new_enrollments || 0,
        activeStudents: rawData?.active_students || 0,
        averageProgress: rawData?.average_progress || 0,
        totalRevenue: rawData?.total_revenue || 0,
        arps: rawData?.arps || 0
      };
      
      console.log("[KPI Service] Parsed edge function response:", parsedData);
      return { data: parsedData, error: null };
    }
    
    // The JSON returned by Supabase RPC has snake_case keys that we need to map to our camelCase interface
    const rawData = result.data as any;
    
    // Map the snake_case fields to our camelCase interface
    const parsedData: StudentKpi = {
      totalStudents: rawData?.total_students || 0,
      newEnrollments: rawData?.new_enrollments || 0,
      activeStudents: rawData?.active_students || 0,
      averageProgress: rawData?.average_progress || 0,
      totalRevenue: rawData?.total_revenue || 0,
      arps: rawData?.arps || 0
    };
    
    devLog("Parsed student KPI data:", parsedData);
    console.log("[KPI Service] Successfully parsed RPC data:", parsedData);
    
    return { 
      data: parsedData, 
      error: null 
    };
  } catch (error) {
    devError("Failed to fetch student KPIs:", error);
    console.error("[KPI Service] Critical error:", error);
    return {
      data: {
        totalStudents: 0,
        newEnrollments: 0,
        activeStudents: 0,
        averageProgress: 0,
        totalRevenue: 0,
        arps: 0
      },
      error
    };
  }
};

export const calculateStudentKpis = (studentsData: any[]): StudentKpi => {
  return {
    totalStudents: studentsData?.length || 0,
    newEnrollments: 0,
    activeStudents: 0,
    averageProgress: 0,
    totalRevenue: 0,
    arps: 0
  };
};
